import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/utils/common.utils";
import { Button } from "@/components/ui/button";

interface NotificationFilterItem {
  label: ReactNode;
  value: string;
  icon?: LucideIcon;
  count?: number;
}

interface NotificationFilterTabsProps {
  items: NotificationFilterItem[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  testIdPrefix?: string;
}

export default function NotificationFilterTabs({ items, value, onValueChange, className, testIdPrefix = "notifications-filter" }: NotificationFilterTabsProps) {
  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {items.map((item) => {
        const active = value === item.value;
        const Icon = item.icon;
        return (
          <Button variant="seamless"
            type="button"
            key={item.value || "all"}
            onClick={() => onValueChange(item.value)}
            className={cn(
              "flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
              active ? "bg-dash-hover text-dash-greenDeep" : "text-dash-muted hover:bg-dash-hover/60 hover:text-dash-ink"
            )}
            aria-pressed={active}
            data-testid={`${testIdPrefix}-${item.value || "all"}`}
          >
            {Icon && <Icon className="size-3.5" />}
            {item.label}
            {/* Counts are absent until the first fetch resolves — an empty pill
                reads as a broken dot, so render nothing until there is a number. */}
            {item.count != null && (
              <span
                className={cn(
                  "flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold",
                  active ? "bg-dash-greenDeep text-white" : "bg-dash-chip text-dash-muted"
                )}
              >
                {item.count}
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
}
